/* eslint-disable ember/classic-decorator-no-classic-methods, ember/require-computed-property-dependencies */
// Standard Library imports
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { get, action, computed } from '@ember/object';
import { intersect, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal imports
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';
import { compact } from '@linc-technologies/linced-common/utils/arrays';

export default class AttendanceTrackerController extends Controller {
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  session;

  queryParams = ['year'];
  @tracked year = moment().format('YYYY');
  @tracked sortProp = 'lastName';
  @tracked sortDir = 'asc';
  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get showLateCount() {
    return this.jurisdiction !== Jurisdiction.AU_NSW;
  }

  @computed('year', 'trackMovements')
  get title() {
    if (this.trackMovements) {
      return `Attendance Tracker (days) ${this.year}`;
    }
    return `Attendance Tracker (Truancy code) ${this.year}`;
  }

  // In-table field filters
  @tracked lastNameFilter = '';

  @tracked firstNameFilter = '';
  @tracked genderFilter = '';
  @tracked homeroomFilter = '';
  @tracked ethnicityFilter = '';

  @tracked fields = Object.freeze([
    'lastName',
    'firstName',
    'gender',
    'homeroom',
    'ethnicity',
  ]);

  // updateFieldFilter writes one of the `<field>Filter` properties above. The
  // field is curried in by `{{fn}}` so the input's value arrives last; those
  // properties are tracked, so the computed-key assignment goes through the
  // tracked setter.
  @action
  updateFieldFilter(field, value) {
    this[`${field}Filter`] = value;
  }

  // fieldFilters combines all in-table filters into an array
  // if the values are non-empty
  @computed(
    'lastNameFilter',
    'firstNameFilter',
    'genderFilter',
    'homeroomFilter',
    'ethnicityFilter',
    'fields',
  )
  get fieldFilters() {
    return this.fields.reduce((filters, key) => {
      let value = this.get(`${key}Filter`);
      if (!isEmpty(value)) {
        filters.push({ key, value });
      }
      return filters;
    }, []);
  }

  @tracked
  attendanceFields = null;

  // initAttendanceFilters is called from the route's setupController hook
  // to initialise the attendance field filters
  // attendanceFields is a plain (sparse) array, not a sequential list: each
  // term's real (numeric) `term.term` is used directly as the array index,
  // so this is native JS sparse-array behaviour, unrelated to A()/EmberArray
  // - reduce/each-in correctly skip unset indices either way. Each element
  // is a genuine EmberObject, which is independently reactive via classic
  // .set()/.get() regardless of what container holds it.
  initAttendanceFilters() {
    let attendanceFields = [];
    this.model.terms
      .filter((item) => item.academicYear === this.model.year)
      .forEach((term) => {
        attendanceFields[term.term] = EmberObject.create({
          present: '',
          presentPercent: '',
          absent: '',
          term: term.term,
        });
      });
    this.attendanceFields = attendanceFields;
  }

  @computed('attendanceFields.@each.{present,presentPercent,absent}')
  get attendanceFilters() {
    return this.attendanceFields.reduce((filters, term) => {
      let termFilters = Object.keys(term).reduce((filters, key) => {
        if (key !== 'term') {
          let value = term[key];
          if (!isEmpty(value)) {
            filters.push({ term: term.term, key, value });
          }
        }
        return filters;
      }, []);
      return filters.concat(termFilters);
    }, []);
  }

  @computed(
    'model.{people.[],tracker.rows.[]}',
    'year',
    'terms.[]',
    'jurisdiction',
  )
  get records() {
    let dataByPerson = {};
    dataByPerson = this.model.tracker.rows.reduce((map, row) => {
      map[row.personId] = row.terms.reduce((terms, term) => {
        if (this.jurisdiction === Jurisdiction.AU_NSW) {
          // if we're in NSW convert to whole days
          term.present = term.present / 2;
          term.absent = term.absent / 2;
        }
        terms[term.term] = term;
        return terms;
      }, EmberObject.create({}));
      return map;
    }, EmberObject.create({}));

    return compact(
      this.model.people.map((person) => {
        let attendance = dataByPerson[person.id];
        let status = person.get('learner.enrolmentStub.status') ?? 0;
        let isCurrent =
          status === EnrolmentStatus.CURRENT ||
          status === EnrolmentStatus.PENDING;
        if (isCurrent || !isEmpty(attendance)) {
          let ethnicity;
          if (this.jurisdiction === Jurisdiction.AU_NSW) {
            ethnicity = person.atsi;
          } else {
            ethnicity = person.displayEthnicity;
          }
          return {
            lastName: person.lastName,
            firstName: person.firstName,
            gender: person.gender,
            homeroom: person.homeroom ? person.homeroom.name : '',
            ethnicity: ethnicity,
            attendance,
            // Return the person to use as a reference for group maker selection
            person,
          };
        }
      }),
    );
  }

  @computed(
    'records.[]',
    'fieldFilters.@each.{key,value}',
    'attendanceFilters.@each.{term,key,value}',
  )
  get filteredRecords() {
    if (isEmpty(this.fieldFilters) && isEmpty(this.attendanceFilters)) {
      // No filters applied
      return this.records;
    } else {
      // We've applied filters on any of the student or attendance fields
      return this.records.filter((record) => {
        return this.filterByFields(record) && this.filterByAttendance(record);
      });
    }
  }

  // filterByFields indicates whether a record is filtered out by any of the student fields filters
  filterByFields(record) {
    return this.fieldFilters.every((filter) => {
      let dataType = DataType.STRING;
      let key = filter.key;
      let value = filter.value;
      return filterValue(record[key], dataType, value);
    });
  }

  // filterByAttendance returns whether a records filtered by any of the terms' attendance
  // statistics filters
  filterByAttendance(record) {
    return this.attendanceFilters.every((filter) => {
      let dataType = DataType.INTEGER;
      let term = filter.term;
      let key = filter.key;
      let value = filter.value;
      // Some records can have an undefined attendance
      let fieldValue = 0;
      if (!isEmpty(record.attendance) && !isEmpty(record.attendance[term])) {
        fieldValue = record.attendance[term][key];
      }
      // Call a filtering util
      return filterValue(fieldValue, dataType, value);
    });
  }

  @computed(
    'filteredRecords.[]',
    'sortProp',
    'sortDir',
    'model.{people.[],tracker.rows.[]}',
    'year',
    'terms.[]',
  )
  get sortedRecords() {
    // Attendance columns (e.g. 'attendance.term1.present') are numeric;
    // everything else falls through to naturalSort's localeCompare path.
    let comparator = naturalSort(this.sortProp, this.sortDir, {
      get: true,
      numeric: (prop) => prop.startsWith('attendance.'),
    });
    return this.filteredRecords.slice().sort(comparator);
  }

  get terms() {
    return this.model.terms.filter((item) => item.academicYear === this.year);
  }

  @computed('sortedRecords.[]', 'terms.[]')
  get exportData() {
    return this.sortedRecords.map((record) => {
      // Start with the student's details.
      let row = [
        record.lastName,
        record.firstName,
        record.gender,
        record.homeroom,
        record.ethnicity,
      ];

      // Add the attendance data for each term.
      this.terms.forEach((term) => {
        let termData = get(record, `attendance.${term.term}`);
        if (isEmpty(termData)) {
          row.push('0', '0%', '0');
        } else {
          let present = termData.present;
          if (this.showLateCount) {
            if (termData.late > 0) {
              present += ` (${termData.late})`;
            }
          }
          row.push(present || 0);
          row.push(`${termData.presentPercent || 0}%`);
          row.push(termData.absent || 0);
        }
      });

      return row;
    });
  }

  // selected holds the array of learners used to create a new group with
  @tracked
  selected = [];

  // clearSelection resets the selected
  clearSelection() {
    this.selected = [];
  }

  // visiblePeople holds the array of people present in the table
  @computed('sortedRecords.[]')
  get visiblePeople() {
    return this.sortedRecords.map((record) => record.person);
  }

  // filteredSelectedPeople filters out people not visible in the table
  // from the selected people
  @intersect('selected', 'visiblePeople')
  filteredSelectedPeople;

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((item) => item !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleAll() {
    if (this.selected.length >= this.sortedRecords.length) {
      // All or more is already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all.
      this.selected = this.sortedRecords.map((item) => item.person);
    }
  }

  @action
  setAttendanceFilter(term, termField, newValue) {
    this.attendanceFields[term].set(termField, newValue);
  }
  resetFilters() {
    this.lastNameFilter = '';
    this.firstNameFilter = '';
    this.genderFilter = '';
    this.homeroomFilter = '';
    this.ethnicityFilter = '';
  }

  reset() {
    this.resetFilters();
    this.clearSelection();
  }
}
